/**
 * ImagePicker — the single shared image field used across the Landing Page
 * editor (Classic sections + block-based designs). Lets an admin type/paste a
 * URL, UPLOAD a new file (stored once, referenced by URL), or PICK an existing
 * image from the centralized library (db.cms_media). Also supports deleting a
 * library image with the in-use safety guard.
 *
 * Images are stored ONLY as URLs — never duplicated. Same endpoints as the
 * legacy uploader so the library stays unified everywhere.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Image as ImageIcon, Images as ImagesIcon, Trash2, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { toast } from 'sonner';
import { invalidateCmsCache } from '../../hooks/useCmsContent';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => {
  if (!u) return '';
  if (/^https?:\/\//.test(u)) return u;
  if (u.startsWith('/api/')) return `${API_URL}${u}`;
  return u;
};

export const ImagePicker = ({ value, onChange, testid, hint = 'Upload (max 5 MB)' }) => {
  const [busy, setBusy] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [library, setLibrary] = useState([]);
  const [libraryLoaded, setLibraryLoaded] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [usageWarn, setUsageWarn] = useState(null);

  const askDelete = (m) => { setDeleteTarget(m); setUsageWarn(null); };
  const doDelete = async (force) => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      const res = await axios.delete(
        `${API_URL}/api/admin/cms/media/${deleteTarget.media_id}`,
        { params: force ? { force: true } : {}, withCredentials: true },
      );
      toast.success('Image deleted');
      if (value === deleteTarget.url) onChange('');
      setLibrary((lib) => lib.filter((x) => x.media_id !== deleteTarget.media_id));
      if (res.data?.references_cleared) { try { invalidateCmsCache(); } catch { /* noop */ } }
      setDeleteTarget(null); setUsageWarn(null);
    } catch (err) {
      if (err.response?.status === 409) {
        const d = err.response.data?.detail;
        setUsageWarn(d && typeof d === 'object' ? d : { message: 'Image is in use', used_in: [] });
      } else {
        const d = err.response?.data?.detail;
        toast.error((d && (d.message || d)) || 'Delete failed');
      }
    } finally { setDeleteBusy(false); }
  };

  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error('Max 5 MB'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('image', f);
      const res = await axios.post(`${API_URL}/api/admin/cms/upload-image`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(res.data?.url || '');
      toast.success('Image uploaded');
      setLibraryLoaded(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  };

  const openLibrary = async () => {
    setLibraryOpen((o) => !o);
    if (libraryLoaded) return;
    try {
      const res = await axios.get(`${API_URL}/api/admin/cms/media?limit=60`, { withCredentials: true });
      setLibrary(res.data?.items || []);
      setLibraryLoaded(true);
    } catch { /* degrade silently — admin can still upload */ }
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://… or /api/uploads/cms/…"
          data-testid={testid}
        />
        <label className="cursor-pointer shrink-0">
          <input type="file" accept="image/*" onChange={handle} className="hidden" data-testid={testid ? `${testid}-file` : undefined} />
          <span className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-[#E8E5DF] hover:bg-[#FAF8F4] text-xs">
            <ImageIcon className="w-3.5 h-3.5" />{busy ? 'Uploading…' : hint}
          </span>
        </label>
        <button
          type="button"
          onClick={openLibrary}
          className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-[#E8E5DF] hover:bg-[#FAF8F4] text-xs shrink-0"
          title="Pick a previously-uploaded image"
          data-testid={testid ? `${testid}-library` : undefined}
        >
          <ImagesIcon className="w-3.5 h-3.5" />Library
        </button>
      </div>
      {value && <img src={resolveUrl(value)} alt="" className="h-20 rounded-md border border-[#E8E5DF] object-cover" />}
      {libraryOpen && (
        <div className="rounded-xl border border-[#E8E5DF] bg-white p-2" data-testid={testid ? `${testid}-library-panel` : undefined}>
          {library.length === 0 ? (
            <p className="text-xs text-[#6B6B7A] text-center py-3">No uploads yet — add your first image above.</p>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-1.5 max-h-56 overflow-y-auto">
              {library.map((m) => (
                <div key={m.media_id || m.url} className="relative group aspect-square">
                  <button
                    type="button"
                    onClick={() => { onChange(m.url); setLibraryOpen(false); toast.success('Image picked from library'); }}
                    className={`w-full h-full rounded-md overflow-hidden border-2 transition-all ${value === m.url ? 'border-[#FF3B6B] ring-2 ring-[#FF3B6B]/30' : 'border-[#E8E5DF] hover:border-[#FF3B6B]'}`}
                    title={`${m.filename_original || ''} · ${m.uploaded_by_name || 'admin'}`}
                    data-testid={`media-${m.media_id || ''}`}
                  >
                    <img src={resolveUrl(m.url)} alt="" className="w-full h-full object-cover" loading="lazy" />
                  </button>
                  {m.media_id && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); askDelete(m); }}
                      className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-white/95 border border-[#E8E5DF] flex items-center justify-center text-[#FF3B6B] shadow-sm opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity hover:bg-[#FFEDF3]"
                      title="Delete image from library"
                      data-testid={`media-delete-${m.media_id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) { setDeleteTarget(null); setUsageWarn(null); } }}>
        <DialogContent className="bg-white max-w-md" data-testid="cms-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              <AlertTriangle className="w-5 h-5 text-[#FF3B6B]" />
              {usageWarn ? 'Image is still in use' : 'Delete this image?'}
            </DialogTitle>
            <DialogDescription className="text-xs text-[#6B6B7A]">
              {usageWarn
                ? 'Deleting it will remove it from the places listed below and may leave those spots blank.'
                : 'This permanently removes the image from your library. This action cannot be undone.'}
            </DialogDescription>
          </DialogHeader>
          {deleteTarget && (
            <div className="flex items-start gap-3">
              <img src={resolveUrl(deleteTarget.url)} alt="" className="h-20 w-20 rounded-md border border-[#E8E5DF] object-cover flex-shrink-0" />
              <div className="text-xs text-[#6B6B7A] min-w-0">
                <p className="truncate font-medium text-[#2B2B33]">{deleteTarget.filename_original || 'Uploaded image'}</p>
                <p className="truncate">{deleteTarget.uploaded_by_name || 'admin'}</p>
              </div>
            </div>
          )}
          {usageWarn && (
            <div className="rounded-lg border border-[#FFD1DE] bg-[#FFF5F8] p-3" data-testid="cms-delete-usage-warn">
              <p className="text-xs font-semibold text-[#B4004E] mb-1.5">{usageWarn.message || 'This image is currently in use.'}</p>
              <ul className="text-xs text-[#6B6B7A] space-y-0.5 list-disc list-inside">
                {(usageWarn.used_in || []).map((u, i) => (
                  <li key={u.section || i}>{u.label || u.section}{u.count > 1 ? ` (×${u.count})` : ''}</li>
                ))}
              </ul>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" type="button" onClick={() => { setDeleteTarget(null); setUsageWarn(null); }} disabled={deleteBusy} data-testid="cms-delete-cancel">Cancel</Button>
            <Button type="button" onClick={() => doDelete(!!usageWarn)} disabled={deleteBusy} className="bg-[#FF3B6B] hover:bg-[#e8305c] text-white" data-testid="cms-delete-confirm">
              {deleteBusy ? 'Deleting…' : usageWarn ? 'Delete anyway (clears references)' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImagePicker;
